import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, TreeDeciduous, AlertTriangle, Phone } from 'lucide-react';
import { apiGet, errorMessage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { greetingForNow } from '../lib/dates';
import { AlertsSection, CriticalBanner } from '../components/dashboard/AlertsSection';
import { PollWidget } from '../components/dashboard/PollWidget';
import { MyReportsSection } from '../components/dashboard/ReportsSections';
import { EventsSection } from '../components/dashboard/EventsSection';
import { LatestNewsSection } from '../components/dashboard/LatestNewsSection';
import type { NewsFeedItem } from '../types';
import type { Alert, Event, PublicPoll, Report } from '../types';
import '../components/news/news-card.css';
import './Dashboard.css';

const QUICK_ACTIONS = [
    { to: '/report', label: 'Report issue', tone: 'warning', icon: <AlertTriangle size={24} aria-hidden="true" /> },
    { to: '/providers', label: 'Find a pro', tone: 'primary', icon: <Wrench size={24} aria-hidden="true" /> },
    { to: '/garden', label: 'Garden', tone: 'success', icon: <TreeDeciduous size={24} aria-hidden="true" /> },
    { to: '/contacts', label: 'Contacts', tone: 'accent', icon: <Phone size={24} aria-hidden="true" /> },
];

/**
 * `GET /api/dashboard` in one request. Every key is loaded independently by the
 * Worker, so any one of them can be null while the rest are fine — each section
 * below renders its own error state for that.
 */
interface DashboardResponse {
    alerts: Alert[] | null;
    poll: PublicPoll | null;
    my_open_reports: Report[] | null;
    my_recent_fixes: Report[] | null;
    events: Event[] | null;
    latest_news: NewsFeedItem[] | null;
}

const EMPTY: DashboardResponse = {
    alerts: null, poll: null, my_open_reports: null, my_recent_fixes: null, events: null, latest_news: null,
};

export function Dashboard() {
    const { profile } = useAuth();
    const [data, setData] = useState<DashboardResponse>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const firstName = profile?.full_name?.trim().split(/\s+/)[0] || 'neighbour';

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await apiGet<DashboardResponse>('/api/dashboard'));
            setError(null);
        } catch (err) {
            console.warn('Dashboard failed', err);
            setData(EMPTY);
            setError(errorMessage(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const section = { loading, error, onRetry: () => { void load(); } };

    return (
        <div className="page dashboard">
            <CriticalBanner alerts={data.alerts ?? []} />

            <header className="dashboard-header">
                <div className="container">
                    <div className="dashboard-greeting">
                        <h1>{greetingForNow()}, {firstName}!</h1>
                        <p>Here's what's happening in Forest Town today.</p>
                    </div>
                </div>
            </header>

            <div className="container dashboard-content">
                <AlertsSection data={data.alerts} {...section} />

                <PollWidget data={data.poll} {...section} />

                <section className="dashboard-section">
                    <div className="section-header">
                        <h2>Quick actions</h2>
                    </div>
                    <nav className="quick-actions-grid" aria-label="Quick actions">
                        {QUICK_ACTIONS.map((qa) => (
                            <Link key={qa.to} to={qa.to} className="quick-action-card">
                                <div className={`quick-action-icon ${qa.tone}`}>{qa.icon}</div>
                                <span>{qa.label}</span>
                            </Link>
                        ))}
                    </nav>
                </section>

                <MyReportsSection open={data.my_open_reports} fixes={data.my_recent_fixes} {...section} />
                <EventsSection data={data.events} {...section} />
                <LatestNewsSection data={data.latest_news} {...section} />
            </div>
        </div>
    );
}
